import './App.css';
import Navigation from './components/navigation/Navigation';
import About from './pages/About';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import Home from './pages/Home';
import Contact from './pages/Contact';
import Footer from './components/footer/Footer';
import Product from './components/product/Product';
import Checkout from './components/checkout/Checkout';
import Order from './components/order/Order';
import FooterHeader from './components/footerHeader/FooterHeader';
import SideBar from './components/sideBar/SideBar';
import DrawerCart from './components/drawer/Drawer';
import Search from './components/search/Search';
import Login from './components/login/Login';
import Register from './components/register/Register';
import ForgetPassword from './components/forgetPassword/ForgetPassword';
import SetNewPassword from './components/forgetPassword/SetNewPassword';
import VerifyOtp from './components/verifyOtp/VerifyOtp';
import { Fragment, useState, useEffect } from 'react';
import User from './components/user/User';
import Dashboard from './components/dashboard/Dashboard';
import MyOrders from './components/myOrders/MyOrders';
import UpdateProfile from './components/updateProfile/UpdateProfile';
import UpdatePassword from './components/updatePassword/UpdatePassword';
import BusinessInformationPage from './pages/BusinessInformation';
import Offers from './pages/Offers';
import Layout from './Layout';
import ProtectedRoutes from './context/ProtectedRoutes';
import { useUserFromLocalStorage } from './store/reducers/userSlice';
import LoadingBar from 'react-top-loading-bar'; // Import the LoadingBar component
import { useSelector } from 'react-redux';
import Landing from './pages/landing';
import PendingApproval from './components/PendingApproval/PendingApproval';
import Reverify from './components/reverify/Reverify';
import { isActiveSession, isUnverifiedSession } from './utils/verification';
import FAQ from './components/faq/faq';
import NotFound from './pages/404';
import Cart from './components/cart/Cart';
import Documentation from './pages/Documentation';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import DeleteAccount from './pages/DeleteAccount';
import { ThemeProvider } from './context/ThemeContext';
import { armWebPushRegistration } from './utils/pushNotifications';
import TawkChat from './components/tawk/TawkChat';


function App() {
	const [progress, setProgress] = useState(0);
	const location = useLocation();
	const navigate = useNavigate(); // Add this hook
	const { user } = useSelector((state) => state.user);

	useUserFromLocalStorage();

	const isRunningAsPWA = () => {
		return (
			window.matchMedia('(display-mode: standalone)').matches ||
			window.navigator.standalone ||
			document.referrer.includes('android-app://')
		);
	};

	useEffect(() => {
		setProgress(30);
		const timer = setTimeout(() => setProgress(100), 500);
		return () => clearTimeout(timer);
	}, [location.pathname]);

	useEffect(() => {
		if (isRunningAsPWA() && !user && location.pathname === '/') {
			navigate('/login'); // Redirect to login
		}
	}, [user, location.pathname, navigate]);

	// FCM web push (#101): register this browser's token on every session
	// start (login or page load with a stored session). No-op until the
	// REACT_APP_FIREBASE_* env vars are baked into the build. Depends on
	// the boolean, not the user object — its identity changes on every
	// login/update dispatch. Registration waits for a user gesture when
	// the permission prompt hasn't been decided yet (#100).
	// An unverified session is capped by the backend to the user's own record,
	// so registering a device token would only 403 (#145).
	const isAuthenticated = isActiveSession(user);
	useEffect(() => {
		if (isAuthenticated) {
			return armWebPushRegistration();
		}
		return undefined;
	}, [isAuthenticated]);

	return (
		<ThemeProvider>
			<Fragment>
				<LoadingBar
					color="#059669"
					height={3}
					progress={progress}
					onLoaderFinished={() => setProgress(0)}
				/>
				{/* Shop chrome (search, cart, notification bell) is pointless for a
				    session that can only re-verify, and each of its calls 403s (#145). */}
				{!isUnverifiedSession(user) && <Navigation />}
				<Routes>
				{/* Public routes */}
				<Route path="/" element={<Landing />} />
				<Route path="/login" element={<Login />} />
				<Route path="/register" element={<Register />} />
				<Route path="/forget-password" element={<ForgetPassword />} />
				<Route path="/forgot-password" element={<ForgetPassword />} />
				<Route path="/verify-otp/:phoneNumber" element={<VerifyOtp />} />
				<Route path="/set-new-password" element={<SetNewPassword />} />
				<Route path="/pending-approval" element={<PendingApproval />} />
				{/* Public on purpose: both app stores require the privacy policy and
				    deletion request pages to be reachable without signing in (#157). */}
				<Route path="/privacy" element={<Privacy />} />
				<Route path="/terms" element={<Terms />} />
				<Route path="/delete-account" element={<DeleteAccount />} />
				{/* Outside ProtectedRoutes: a rejected buyer is signed in but capped
				    by the backend to re-submitting their own documents (#145). */}
				<Route path="/reverify" element={<Reverify />} />
				{/* Protected routes */}
				<Route path="/" element={<Layout />}>
					<Route element={<ProtectedRoutes user={user} />}>
						<Route path='home' element={<Home />} />
						<Route path="about-us" element={<About />} />
						<Route path="contact-us" element={<Contact />} />
						<Route path="documentation" element={<Documentation />} />
						<Route path="product/:productTitle" element={<Product />} />
						<Route path="search" element={<Search />} />
						<Route path="order/:id" element={<Order />} />
						<Route path="checkout" element={<Checkout />} />
						<Route path="offer" element={<Offers />} />
						<Route path="faq" element={<FAQ />} />
						<Route path="user" element={<User />}>
							<Route path="dashboard" element={<Dashboard />} />
							<Route path="my-orders" element={<MyOrders />} />
							<Route path="update-profile" element={<UpdateProfile />} />
							<Route path="update-password" element={<UpdatePassword />} />
							<Route path="business-information" element={<BusinessInformationPage />} />
						</Route>
					</Route>
					<Route path="*" element={<NotFound />} />
				</Route>
			</Routes>

			<div>
				<FooterHeader />
				<SideBar />
				<DrawerCart />
				{isActiveSession(user) && <Cart />}
			</div>
			<div className="w-full">
				<Footer />
			</div>
			{/* Site-wide, including the login and reverify screens — a rejected
			    buyer needs support most (#152). */}
			<TawkChat />
		</Fragment>
	</ThemeProvider>
	);
}

export default App;

